import React, { useState, useRef, useContext, useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { ContextPage } from "../page/pageContext";
import { ContextContent } from "./contentContext";
import { setIsLoad } from "../../../app/reducers/app/actions";

// Сравнение значения формы (строки) со значением сервера (числа/даты/null).
const isEqualValue = (valueA, valueB) => {
    const normalize = (value) => (value === null || value === undefined) ? '' : value;
    const a = normalize(valueA);
    const b = normalize(valueB);
    if (a instanceof Date || b instanceof Date) {
        return String(a instanceof Date ? a.toISOString() : a) === String(b instanceof Date ? b.toISOString() : b);
    }
    if (typeof a === 'object' || typeof b === 'object') return JSON.stringify(a) === JSON.stringify(b);
    return String(a) === String(b);
};

// Ядро форм show/edit/create. Страница декларирует поля и контракт
// (_dataToServer / _handleSetData), провайдер сам ходит в ApiModel.
// withEdit={false} — раздел без редактирования (карандаш не рисуется);
// withDestroy={false} — в API раздела нет удаления (кнопка «Удалить» не рисуется нигде);
// withDestroyOnShow — кнопка удаления прямо в show-режиме (разделы без edit, но с destroy).
export default function ProviderContent({
    ApiModel,
    type: typeDefault = 'show',
    withEdit = true,
    withDestroy = true,
    withDestroyOnShow = false,
    handleDoCreate,
    handleDoDelete,
    children,
}) {
    const contextPage = useContext(ContextPage);
    const dispatch = useDispatch();

    const [type, setType] = useState(typeDefault);
    const [codeApi, setCodeApi] = useState(null);
    const [errors, setErrors] = useState(null);

    const _dataFromServer = useRef({});   // последняя запись с сервера (форматированная)
    const _dataToServer = useRef({});     // что страница отправит при create/update
    // Поля, которые backend-валидация требует в КАЖДОМ update (required-правила) —
    // уходят вместе с diff; пустой diff по-прежнему не шлёт ничего.
    const _dataRequiredToServer = useRef({});
    const _handleSetData = useRef(null);  // как разложить запись по state страницы

    // Навигация на другую запись/режим — сбрасываем тип к пропсу
    useEffect(() => {
        setType(typeDefault);
        setCodeApi(null);
    }, [typeDefault, contextPage.id]);

    const doLoad = useCallback(async () => {
        // create и списки ничего не грузят; id может отсутствовать (ресурс-одиночка — профиль)
        if (typeDefault === 'create' || typeDefault === 'create-child' || typeDefault === 'index') return;
        if (!ApiModel) return;
        dispatch(setIsLoad(true));
        try {
            const api = ApiModel.Show(contextPage.id);
            const data = await api.getResponse();
            setCodeApi(api.code);
            if (data) {
                _dataFromServer.current = data;
                if (_handleSetData.current) _handleSetData.current(data);
            }
        } finally {
            dispatch(setIsLoad(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ApiModel, contextPage.id, typeDefault, dispatch]);

    // PATCH только изменившихся полей; пустой diff — предупреждение без запроса.
    const doUpdate = useCallback(async () => {
        if (!ApiModel) return;
        const dataToServer = _dataToServer.current ?? {};
        const diff = {};
        for (const key in dataToServer) {
            if (!isEqualValue(dataToServer[key], _dataFromServer.current[key])) diff[key] = dataToServer[key];
        }
        if (Object.keys(diff).length === 0) {
            new ApiModel().addMessages('Ничего не поменялось', 'warning');
            return;
        }
        dispatch(setIsLoad(true));
        try {
            const api = ApiModel.Update(contextPage.id, { ..._dataRequiredToServer.current, ...diff });
            const data = await api.getResponse();
            setCodeApi(api.code);
            setErrors(api.errors);
            if (api.code >= 200 && api.code <= 299) {
                if (data) {
                    _dataFromServer.current = data;
                    if (_handleSetData.current) _handleSetData.current(data);
                } else {
                    await doLoad();
                }
                setType('show');
            }
        } finally {
            dispatch(setIsLoad(false));
        }
    }, [ApiModel, contextPage.id, dispatch, doLoad]);

    const doCreate = useCallback(async () => {
        if (!ApiModel) return;
        dispatch(setIsLoad(true));
        try {
            const api = ApiModel.Store(_dataToServer.current ?? {});
            const data = await api.getResponse();
            setCodeApi(api.code);
            setErrors(api.errors);
            if (api.code >= 200 && api.code <= 299) {
                setType('show');
                if (handleDoCreate) handleDoCreate(data);
            }
        } finally {
            dispatch(setIsLoad(false));
        }
    }, [ApiModel, dispatch, handleDoCreate]);

    const doDelete = useCallback(async () => {
        if (!ApiModel) return;
        dispatch(setIsLoad(true));
        try {
            const api = ApiModel.Destroy(contextPage.id);
            await api.getResponse();
            setCodeApi(api.code);
            if (api.code >= 200 && api.code <= 299 && handleDoDelete) handleDoDelete();
        } finally {
            dispatch(setIsLoad(false));
        }
    }, [ApiModel, contextPage.id, dispatch, handleDoDelete]);

    // Откат несохранённых правок к данным сервера (выход из режима edit)
    const reSetData = useCallback(() => {
        if (_handleSetData.current && _dataFromServer.current) {
            _handleSetData.current(_dataFromServer.current);
        }
        setErrors(null);
    }, []);

    const value = useMemo(() => ({
        type,
        setType,
        isShow: type === 'show',
        isEdit: type === 'edit',
        isCreate: type === 'create',
        isCreateChild: type === 'create-child',
        isCreateOrEdit: type === 'edit' || type === 'create' || type === 'create-child',
        isWithEdit: withEdit,
        isWithDestroy: withDestroy,
        isWithDestroyOnShow: withDestroyOnShow,
        _dataFromServer,
        _dataToServer,
        _dataRequiredToServer,
        _handleSetData,
        codeApi,
        errors,
        doLoad,
        doUpdate,
        doCreate,
        doDelete,
        reSetData,
        setTitle: contextPage.setTitle,
        checkPermission: contextPage.checkPermission,
    }), [
        type, withEdit, withDestroy, withDestroyOnShow, codeApi, errors,
        doLoad, doUpdate, doCreate, doDelete, reSetData,
        contextPage.setTitle, contextPage.checkPermission,
    ]);

    return <ContextContent.Provider value={value}>{children}</ContextContent.Provider>;
}
